import './TeamScreen.css';
import { COACH } from '@/constants/coach';

/**
 * Team page — deliberately incomplete per brief §4.6, but populated with a
 * manager-dashboard mockup so the demo can paint the picture of what
 * coaching analytics look like at scale.
 *
 * Every section below is illustrative mock data, rendered behind the same
 * "preview · not live yet" hatch overlay used on the roster. No state, no
 * handlers, no real metrics — the goal is to communicate the SHAPE of the
 * manager experience without committing to build it for the demo. Override
 * #2 still applies (banner is vague "Coming soon," never dated).
 */

type PersonaKey = 'tyler' | 'priya' | 'ron';
type MasteryStatus = 'mastered' | 'practicing' | 'untouched';

interface Teammate {
  initials: string;
  name: string;
  role: string;
  sessions: number;
  avg_score: number;
  /** Per-persona depth — how many attempts, best score, mastery state. */
  personas: Record<PersonaKey, { attempts: number; best: number; status: MasteryStatus }>;
}

const TEAMMATES: ReadonlyArray<Teammate> = [
  {
    initials: 'SL',
    name: 'Sharon L.',
    role: 'Enterprise sales',
    sessions: 24,
    avg_score: 82,
    personas: {
      tyler: { attempts: 12, best: 91, status: 'mastered' },
      priya: { attempts: 9, best: 88, status: 'mastered' },
      ron: { attempts: 3, best: 72, status: 'practicing' },
    },
  },
  {
    initials: 'GB',
    name: 'Gerald B.',
    role: 'Enterprise sales',
    sessions: 18,
    avg_score: 75,
    personas: {
      tyler: { attempts: 8, best: 90, status: 'mastered' },
      priya: { attempts: 7, best: 85, status: 'mastered' },
      ron: { attempts: 3, best: 64, status: 'practicing' },
    },
  },
  {
    initials: 'PR',
    name: 'Priya R.',
    role: 'Enterprise sales',
    sessions: 21,
    avg_score: 79,
    personas: {
      tyler: { attempts: 7, best: 92, status: 'mastered' },
      priya: { attempts: 8, best: 87, status: 'mastered' },
      ron: { attempts: 6, best: 86, status: 'mastered' },
    },
  },
  {
    initials: 'MC',
    name: 'Marcus C.',
    role: 'Enterprise sales',
    sessions: 14,
    avg_score: 71,
    personas: {
      tyler: { attempts: 8, best: 88, status: 'mastered' },
      priya: { attempts: 5, best: 74, status: 'practicing' },
      ron: { attempts: 1, best: 51, status: 'practicing' },
    },
  },
  {
    initials: 'DL',
    name: 'David L.',
    role: 'Enterprise sales',
    sessions: 9,
    avg_score: 64,
    personas: {
      tyler: { attempts: 6, best: 86, status: 'mastered' },
      priya: { attempts: 3, best: 62, status: 'practicing' },
      ron: { attempts: 0, best: 0, status: 'untouched' },
    },
  },
  {
    initials: 'TM',
    name: 'Tanya M.',
    role: 'Enterprise sales',
    sessions: 17,
    avg_score: 77,
    personas: {
      tyler: { attempts: 6, best: 89, status: 'mastered' },
      priya: { attempts: 8, best: 86, status: 'mastered' },
      ron: { attempts: 3, best: 70, status: 'practicing' },
    },
  },
  {
    initials: 'HK',
    name: 'Hassan K.',
    role: 'Enterprise sales',
    sessions: 12,
    avg_score: 70,
    personas: {
      tyler: { attempts: 7, best: 88, status: 'mastered' },
      priya: { attempts: 4, best: 71, status: 'practicing' },
      ron: { attempts: 1, best: 58, status: 'practicing' },
    },
  },
  {
    initials: 'ZW',
    name: 'Zoe W.',
    role: 'Enterprise sales',
    sessions: 6,
    avg_score: 58,
    personas: {
      tyler: { attempts: 5, best: 78, status: 'practicing' },
      priya: { attempts: 1, best: 49, status: 'practicing' },
      ron: { attempts: 0, best: 0, status: 'untouched' },
    },
  },
];

/** Last-eight-weeks average team score — drives the trend sparkline. */
const TEAM_TREND: ReadonlyArray<{ week: string; score: number }> = [
  { week: 'W1', score: 64 },
  { week: 'W2', score: 66 },
  { week: 'W3', score: 65 },
  { week: 'W4', score: 70 },
  { week: 'W5', score: 73 },
  { week: 'W6', score: 71 },
  { week: 'W7', score: 76 },
  { week: 'W8', score: 79 },
];

/** Skill confidence averaged across the team. */
const TEAM_SKILLS: ReadonlyArray<{
  skill: string;
  team_avg: number;
  top_rep: string;
}> = [
  { skill: 'Discovery questions', team_avg: 0.78, top_rep: 'Priya R.' },
  { skill: 'Pain quantification', team_avg: 0.61, top_rep: 'Sharon L.' },
  { skill: 'Objection reframe', team_avg: 0.54, top_rep: 'Tanya M.' },
  { skill: 'Small-yes closes', team_avg: 0.72, top_rep: 'Priya R.' },
  { skill: 'Composure under pressure', team_avg: 0.66, top_rep: 'Gerald B.' },
];

/** Reps the system flagged for a coach's attention this week. */
const AT_RISK: ReadonlyArray<{
  initials: string;
  name: string;
  issue: string;
  action: string;
}> = [
  {
    initials: 'ZW',
    name: 'Zoe W.',
    issue: 'No sessions in 14 days',
    action: "Pair with Priya for a Priya warm-up — Priya's the team's strongest reframer.",
  },
  {
    initials: 'DL',
    name: 'David L.',
    issue: 'Avg score 64 — below team baseline',
    action: 'Re-assign Lesson 4 (Skepticism into scope) before the next Priya attempt.',
  },
  {
    initials: 'HK',
    name: 'Hassan K.',
    issue: 'Hits "small yes" but never escalates to "commitment"',
    action: `Coaching moment with ${COACH.firstName} on closing escalation — 8 minutes.`,
  },
];

/** Most-saved learner moments across the team this quarter. */
const TOP_PLAYS: ReadonlyArray<{
  quote: string;
  saved_by: string;
  saves_count: number;
  tag: string;
}> = [
  {
    quote: "Your ops team is firefighting through the lunch rush, aren't they?",
    saved_by: 'Sharon L.',
    saves_count: 11,
    tag: 'Discovery',
  },
  {
    quote: "Fair — what does 'fine' cost you in a lunch rush?",
    saved_by: 'Priya R.',
    saves_count: 9,
    tag: 'Objection reframe',
  },
  {
    quote: "What's the tax actually paying for that isn't working?",
    saved_by: 'Tanya M.',
    saves_count: 7,
    tag: 'Pain quantification',
  },
];

const FEATURES = [
  {
    title: 'Manager dashboards',
    body: 'See where your team is struggling before the QBR — not after.',
  },
  {
    title: 'Shared playbooks',
    body: "Your top rep's best moments, available to the whole team.",
  },
  {
    title: 'Coached rollouts',
    body: 'New sellers get assigned to personas based on their territory and tenure.',
  },
];

/* ----------------------------- Derived stats ---------------------------- */

const ACTIVE_THIS_WEEK = 6; // illustrative
const SESSIONS_THIS_WEEK = 27;
const TEAM_AVG = Math.round(TEAMMATES.reduce((sum, t) => sum + t.avg_score, 0) / TEAMMATES.length);
const MASTERY_TOTAL = TEAMMATES.length * 3; // 3 personas
const MASTERY_HIT = TEAMMATES.reduce(
  (sum, t) =>
    sum +
    (['tyler', 'priya', 'ron'] as const).filter((p) => t.personas[p].status === 'mastered')
      .length,
  0,
);

/* ------------------------------- Screen -------------------------------- */

export default function TeamScreen() {
  return (
    <div className="team">
      <section className="team__banner" role="note">
        <p className="team__banner-title">Team features are coming soon.</p>
        <p className="team__banner-sub">
          Pilot customers get early access — talk to your Comcast Business contact.
        </p>
      </section>

      <KpiStrip />
      <TrendCard />

      <div className="team__row team__row--two">
        <MasteryHeatmap />
        <SkillsBreakdown />
      </div>

      <RosterCard />

      <div className="team__row team__row--two">
        <AtRiskCard />
        <TopPlaysCard />
      </div>

      <FeaturesRoadmap />
    </div>
  );
}

/* -------------------------------- KPIs --------------------------------- */

function KpiStrip() {
  return (
    <section>
      <p className="eyebrow team__section-eyebrow">Preview · not live yet</p>
      <h1 className="team__section-head">This week, at a glance</h1>
      <PreviewWrap>
        <ul className="kpi-strip">
          <Kpi
            label="Active reps"
            value={`${ACTIVE_THIS_WEEK}`}
            sub="of 8 this week"
            tone="platform"
          />
          <Kpi
            label="Sessions"
            value={`${SESSIONS_THIS_WEEK}`}
            sub="completed in 7 days"
            tone="coach"
          />
          <Kpi label="Team avg score" value={`${TEAM_AVG}`} sub="+5 vs. last week" tone="win" />
          <Kpi
            label="Persona mastery"
            value={`${MASTERY_HIT}`}
            sub={`of ${MASTERY_TOTAL} possible`}
            tone="warmth"
          />
        </ul>
      </PreviewWrap>
    </section>
  );
}

function Kpi({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub: string;
  tone: 'platform' | 'coach' | 'win' | 'warmth';
}) {
  return (
    <li className={`kpi kpi--${tone}`}>
      <p className="kpi__label">{label}</p>
      <p className="kpi__value">{value}</p>
      <p className="kpi__sub">{sub}</p>
    </li>
  );
}

/* ------------------------------- Trend --------------------------------- */

function TrendCard() {
  const max = Math.max(...TEAM_TREND.map((p) => p.score));
  const min = Math.min(...TEAM_TREND.map((p) => p.score));
  // Map values to a 0..1 fraction inside a 100-pt y range so the curve
  // breathes — anchor min to 10% and max to 90%.
  const yFor = (v: number) => {
    if (max === min) return 50;
    const frac = (v - min) / (max - min);
    return 90 - frac * 80;
  };
  const xStep = 100 / (TEAM_TREND.length - 1);
  const points = TEAM_TREND.map((p, i) => `${i * xStep},${yFor(p.score)}`).join(' ');
  return (
    <section>
      <p className="eyebrow team__section-eyebrow">Preview · not live yet</p>
      <h2 className="team__section-head">Eight-week team-score trend</h2>
      <PreviewWrap>
        <div className="trend">
          <div className="trend__chart">
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
              <polyline
                points={points}
                fill="none"
                stroke="var(--color-platform)"
                strokeWidth="1.6"
                vectorEffect="non-scaling-stroke"
              />
              {TEAM_TREND.map((p, i) => (
                <circle
                  key={p.week}
                  cx={i * xStep}
                  cy={yFor(p.score)}
                  r="1.6"
                  fill="var(--color-platform)"
                  vectorEffect="non-scaling-stroke"
                />
              ))}
            </svg>
          </div>
          <ul className="trend__axis">
            {TEAM_TREND.map((p) => (
              <li key={p.week} className="trend__tick">
                <span className="trend__tick-week">{p.week}</span>
                <span className="trend__tick-score">{p.score}</span>
              </li>
            ))}
          </ul>
          <p className="trend__readout">
            Team is up <strong>+15 points</strong> over the eight weeks since Sales Academy
            launched. Biggest jump: W3 → W4, when Lesson 4 (Skepticism into scope) shipped.
          </p>
        </div>
      </PreviewWrap>
    </section>
  );
}

/* --------------------------- Persona heatmap --------------------------- */

function MasteryHeatmap() {
  const personas: PersonaKey[] = ['tyler', 'priya', 'ron'];
  const personaLabels: Record<PersonaKey, string> = {
    tyler: 'Tyler',
    priya: 'Priya',
    ron: 'Ron',
  };
  return (
    <section>
      <p className="eyebrow team__section-eyebrow">Preview · not live yet</p>
      <h2 className="team__section-head">Persona coverage</h2>
      <PreviewWrap>
        <div className="heatmap">
          <div className="heatmap__head">
            <span className="heatmap__head-rep">Rep</span>
            {personas.map((p) => (
              <span key={p} className={`heatmap__head-persona heatmap__head-persona--${p}`}>
                {personaLabels[p]}
              </span>
            ))}
          </div>
          <ul className="heatmap__rows">
            {TEAMMATES.map((t) => (
              <li key={t.name} className="heatmap__row">
                <span className="heatmap__rep">
                  <span className="team__avatar team__avatar--small" aria-hidden>
                    {t.initials}
                  </span>
                  <span className="heatmap__rep-name">{t.name}</span>
                </span>
                {personas.map((p) => {
                  const cell = t.personas[p];
                  return (
                    <span
                      key={p}
                      className={`heatmap__cell heatmap__cell--${cell.status} heatmap__cell--${p}`}
                      title={`${cell.attempts} attempts · best ${cell.best || '—'}`}
                    >
                      <span className="heatmap__cell-best">
                        {cell.attempts > 0 ? cell.best : '—'}
                      </span>
                      <span className="heatmap__cell-attempts">
                        {cell.attempts > 0 ? `${cell.attempts} att.` : 'no attempts'}
                      </span>
                    </span>
                  );
                })}
              </li>
            ))}
          </ul>
          <p className="heatmap__legend">
            Color shows mastery state.{' '}
            <span className="heatmap__legend-dot heatmap__legend-dot--mastered" /> Mastered ·
            <span className="heatmap__legend-dot heatmap__legend-dot--practicing" /> Practicing ·
            <span className="heatmap__legend-dot heatmap__legend-dot--untouched" /> Untouched
          </p>
        </div>
      </PreviewWrap>
    </section>
  );
}

/* --------------------------- Skill distribution ------------------------ */

function SkillsBreakdown() {
  return (
    <section>
      <p className="eyebrow team__section-eyebrow">Preview · not live yet</p>
      <h2 className="team__section-head">Where the team is strong</h2>
      <PreviewWrap>
        <ul className="skills">
          {TEAM_SKILLS.map((s) => (
            <li key={s.skill} className="skills__row">
              <div className="skills__head">
                <span className="skills__name">{s.skill}</span>
                <span className="skills__avg">{Math.round(s.team_avg * 100)}%</span>
              </div>
              <div className="skills__bar">
                <div
                  className="skills__fill"
                  style={{ width: `${Math.round(s.team_avg * 100)}%` }}
                />
              </div>
              <p className="skills__top">Top: {s.top_rep}</p>
            </li>
          ))}
        </ul>
      </PreviewWrap>
    </section>
  );
}

/* -------------------------------- Roster ------------------------------- */

function RosterCard() {
  return (
    <section>
      <p className="eyebrow team__section-eyebrow">Preview · not live yet</p>
      <h2 className="team__section-head">Your team at a glance</h2>
      <PreviewWrap>
        <div className="team__table-head">
          <span>Teammate</span>
          <span>Sessions</span>
          <span>Avg score</span>
          <span>Personas mastered</span>
        </div>
        <ul className="team__roster">
          {TEAMMATES.map((t) => (
            <li key={t.name} className="team__row team__roster-row">
              <div className="team__who">
                <span className="team__avatar" aria-hidden>
                  {t.initials}
                </span>
                <div className="team__who-meta">
                  <p className="team__name">{t.name}</p>
                  <p className="team__role">{t.role}</p>
                </div>
              </div>
              <span className="team__num">{t.sessions}</span>
              <span className="team__num">{t.avg_score}</span>
              <span className="team__mastery">
                {(['tyler', 'priya', 'ron'] as const).map((p) => (
                  <span
                    key={p}
                    className={`team__dot team__dot--${p} ${
                      t.personas[p].status === 'mastered' ? '' : 'team__dot--empty'
                    }`}
                    title={p}
                  />
                ))}
              </span>
            </li>
          ))}
        </ul>
      </PreviewWrap>
    </section>
  );
}

/* ------------------------------- At-risk ------------------------------- */

function AtRiskCard() {
  return (
    <section>
      <p className="eyebrow team__section-eyebrow">Preview · not live yet</p>
      <h2 className="team__section-head">Reps who need a nudge</h2>
      <PreviewWrap>
        <ul className="at-risk">
          {AT_RISK.map((r) => (
            <li key={r.name} className="at-risk__row">
              <span className="team__avatar" aria-hidden>
                {r.initials}
              </span>
              <div className="at-risk__body">
                <p className="at-risk__name">{r.name}</p>
                <p className="at-risk__issue">{r.issue}</p>
                <p className="at-risk__action">
                  <span className="at-risk__action-label">Suggested next step</span> {r.action}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </PreviewWrap>
    </section>
  );
}

/* ------------------------------ Top plays ------------------------------ */

function TopPlaysCard() {
  return (
    <section>
      <p className="eyebrow team__section-eyebrow">Preview · not live yet</p>
      <h2 className="team__section-head">Most-saved plays this quarter</h2>
      <PreviewWrap>
        <ul className="top-plays">
          {TOP_PLAYS.map((p) => (
            <li key={p.quote} className="top-plays__row">
              <span className="top-plays__rank-tag">{p.tag}</span>
              <blockquote className="top-plays__quote">"{p.quote}"</blockquote>
              <p className="top-plays__meta">
                Originated by <strong>{p.saved_by}</strong> · saved by{' '}
                <strong>{p.saves_count}</strong> teammates
              </p>
            </li>
          ))}
        </ul>
      </PreviewWrap>
    </section>
  );
}

/* ----------------------------- Roadmap row ----------------------------- */

function FeaturesRoadmap() {
  return (
    <section>
      <p className="eyebrow team__section-eyebrow">What ships with team</p>
      <h2 className="team__section-head team__section-head--sub">Three features on the roadmap</h2>
      <ul className="team__features">
        {FEATURES.map((f) => (
          <li key={f.title} className="team__feature">
            <p className="team__feature-title">{f.title}</p>
            <p className="team__feature-body">{f.body}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* --------------------------- Preview wrapper --------------------------- */

/**
 * Wraps a preview surface with the diagonal hatch overlay used everywhere
 * on the team page to signal "not live yet." Children render at partial
 * opacity behind the hatch.
 */
function PreviewWrap({ children }: { children: React.ReactNode }) {
  return (
    <div className="team__preview">
      <div className="team__preview-hatch" />
      <div className="team__preview-inner">{children}</div>
    </div>
  );
}
